/**
 * Import Orchestration Service
 * 
 * Responsible for orchestrating the import process to CRM.
 * Follows Single Responsibility Principle by handling only import orchestration.
 */

/* global Office */

import { CustomPropertyService } from './custom-property-service.js';

/**
 * Result object for import operations
 */
export class ImportResult {
  constructor(success, activityResponse, attachmentResults = null, error = null) {
    this.success = success;
    this.activityResponse = activityResponse;
    this.attachmentResults = attachmentResults;
    this.error = error;
  }

  /**
   * Get the activity ID from the response
   */
  getActivityId() {
    if (!this.activityResponse) return null;
    return this.activityResponse.activity || 
           this.activityResponse._id || 
           this.activityResponse.id || 
           this.activityResponse.activityId;
  }

  /**
   * Check if import was successful
   */
  isSuccessful() {
    return this.success && !this.error;
  }
}

/**
 * Strategy for importing different item types
 */
class ImportStrategy {
  async import(item, contacts, leads, campaigns, sharedWith, actionType) {
    throw new Error('import() must be implemented by subclass');
  }
}

/**
 * Email import strategy
 */
class EmailImportStrategy extends ImportStrategy {
  constructor(apiService) {
    super();
    this.apiService = apiService;
  }

  async import(item, contacts, leads, campaigns, sharedWith, actionType) {
    return await this.apiService.linkEmailToContact(contacts, item, leads, campaigns, sharedWith, actionType);
  }
}

/**
 * Appointment import strategy
 */
class AppointmentImportStrategy extends ImportStrategy {
  constructor(apiService) {
    super();
    this.apiService = apiService;
  }

  async import(item, contacts, leads, campaigns, sharedWith, actionType) {
    return await this.apiService.linkAppointmentToContact(contacts, item, leads, campaigns, sharedWith, actionType);
  }
}

/**
 * Factory for creating import strategies
 */
class ImportStrategyFactory {
  constructor(apiService) {
    this.apiService = apiService;
  }

  create(itemType) {
    if (itemType === Office.MailboxEnums.ItemType.Appointment) {
      return new AppointmentImportStrategy(this.apiService);
    }
    return new EmailImportStrategy(this.apiService);
  }
}

/**
 * Service for processing attachments
 */
class AttachmentProcessor {
  constructor(apiService) {
    this.apiService = apiService;
  }

  /**
   * Process attachments for an activity
   * 
   * @param {Object} item - Outlook item
   * @param {string} activityId - Activity ID
   * @param {Array} selectedAttachments - Selected attachment IDs
   * @returns {Promise<Object|null>} Attachment results or null
   */
  async process(item, activityId, selectedAttachments) {
    if (!selectedAttachments || selectedAttachments.length === 0) {
      return null;
    }

    if (!activityId) {
      console.error('No activity ID provided for attachment processing');
      return null;
    }

    try {
      return await this.apiService.processEmailAttachments(item, activityId, selectedAttachments);
    } catch (error) {
      console.error('Attachment processing failed:', error);
      // Don't fail the entire import if attachments fail
      return null;
    }
  }
}

/**
 * Main service for orchestrating CRM imports
 * Follows Single Responsibility Principle and Open/Closed Principle
 */
export class ImportOrchestrator {
  constructor(apiService = null) {
    this.apiService = apiService;
    this.strategyFactory = null;
    this.attachmentProcessor = null;
  }

  /**
   * Initialize the orchestrator with API service
   * Uses lazy loading to avoid circular dependencies
   */
  async _ensureInitialized() {
    if (!this.apiService) {
      const apiModule = await import('./api.js');
      this.apiService = {
        linkEmailToContact: (contacts, item, leads, campaigns, sharedWith, actionType) => 
          apiModule.linkEmailToContact(contacts, item, leads, campaigns, sharedWith, actionType),
        linkAppointmentToContact: (contacts, item, leads, campaigns, sharedWith, actionType) => 
          apiModule.linkAppointmentToContact(contacts, item, leads, campaigns, sharedWith, actionType),
        processEmailAttachments: apiModule.processEmailAttachments
      };
    }

    if (!this.strategyFactory) {
      this.strategyFactory = new ImportStrategyFactory(this.apiService);
    }

    if (!this.attachmentProcessor) {
      this.attachmentProcessor = new AttachmentProcessor(this.apiService);
    }
  }

  /**
   * Import an Outlook item to CRM
   * 
   * @param {Object} item - Outlook item (email or appointment)
   * @param {Array} selectedContacts - Selected contacts
   * @param {Array} selectedLeads - Selected leads
   * @param {Array} selectedCampaigns - Selected campaigns
   * @param {Array} selectedAttachments - Selected attachments
   * @param {Array} sharedWith - Selected users to notify (sharedWith field)
   * @param {string} actionType - Optional activity type. Blank keeps the CRM default.
   * @returns {Promise<ImportResult>} Import result
   */
  async import(item, selectedContacts, selectedLeads, selectedCampaigns, selectedAttachments, sharedWith = [], actionType = '') {
    await this._ensureInitialized();

    try {
      // Import the activity
      const activityResponse = await this._importActivity(
        item, 
        selectedContacts, 
        selectedLeads, 
        selectedCampaigns,
        sharedWith,
        actionType
      );

      // Process attachments
      const attachmentResults = await this._processAttachments(
        item, 
        activityResponse, 
        selectedAttachments
      );

      // Store the activityId on the Outlook item for reliable "View Activity"
      const activityId = this._extractActivityId(activityResponse);
      if (activityId) {
        await CustomPropertyService.setImportData(item, activityId);
      }

      return new ImportResult(true, activityResponse, attachmentResults);

    } catch (error) {
      console.error('Import failed:', error);
      return new ImportResult(false, null, null, error);
    }
  }

  /**
   * Import activity to CRM
   */
  async _importActivity(item, selectedContacts, selectedLeads, selectedCampaigns, sharedWith, actionType) {
    const strategy = this.strategyFactory.create(item.itemType);
    return await strategy.import(item, selectedContacts, selectedLeads, selectedCampaigns, sharedWith, actionType);
  }

  /**
   * Process attachments for the imported activity
   */
  async _processAttachments(item, activityResponse, selectedAttachments) {
    if (!selectedAttachments || selectedAttachments.length === 0) {
      return null;
    }

    const activityId = this._extractActivityId(activityResponse);
    return await this.attachmentProcessor.process(item, activityId, selectedAttachments);
  }

  /**
   * Extract activity ID from response
   */
  _extractActivityId(response) {
    if (!response) return null;
    return response.activity || 
           response._id || 
           response.id || 
           response.activityId;
  }
}

